<template>
    <div>
        <label class="block text-sm font-medium leading-5 text-gray-800 relative mb-1"
            :class="{'required': required}"
            :for="name"
            v-if="label">
            {{ label }}
            <font-awesome-icon class="text-xs text-black ml-1 cursor-pointer"
                :icon="['fas', 'question-circle']"
                style="margin-top: 2px;"
                v-if="tooltip" v-tooltip="tooltip" />
        </label>
        <ValidationProvider :name="label" mode="lazy" :rules="rules" v-slot="{ errors }" tag="div">
            <div class="group w-full bg-white border-gray-400 border transition duration-150 ease-in-out sm:text-sm leading-none text-gray-700 relative cursor-pointer flex items-center focus:outline-none focus:border-gray-500"
                :class="{ 'border-red-700' : errors[0], 'opacity-50 cursor-not-allowed' : disabled, 'compact-select': compact }"
                v-click-outside="closeSelect"
                style="height: 39px;"
                tabindex="0"
                v-on:keydown.down="navigateOptions('down')"
                v-on:keydown.up="navigateOptions('up')"
                v-on:keydown.enter="selectOptionByKey"
                v-on:keydown.esc="closeSelectByKey">
                <span class="w-full py-3 px-3 whitespace-no-wrap overflow-hidden"
                    :class="{'text-gray-500': !selected.label || selectOpen, 'compact text-xs': compact }"
                    v-show="search === ''"
                    @click="openSelect">
                    {{ selected.label ? selected.label : placeholder ? placeholder : 'Select an option' }}
                </span>
                <span v-if="!selectOpen" class="absolute right-0 text-gray-700 z-10 bg-white p-2 mr-px" :class="{ 'compact' : compact }">
                    <font-awesome-icon class="text-gray-700 w-6"
                        :icon="['fas', 'chevron-down']"
                        @click="openSelect"/>
                </span>
                <span v-else class="absolute right-0 text-gray-700 z-10 bg-white p-2" :class="{ 'compact' : compact }">
                    <font-awesome-icon class="text-gray-700 w-6"
                        :icon="['fas', 'chevron-up']"
                        @click="closeSelect"/>
                </span>
                <span class="absolute right-0 mr-6 text-gray-700 z-10 bg-white p-2" :class="{ 'compact' : compact }">
                    <font-awesome-icon class="text-gray-700 w-6"
                        :icon="['fas', 'times']"
                        @click="clearSelect"
                        v-if="selected.value !== ''"/>
                </span>

                <input type="text"
                    ref="searchInput"
                    v-model="search"
                    class="w-full absolute block bg-transparent outline-none px-3 py-4"
                    data-lpignore="true"
                    v-if="selectOpen"
                    v-on:keydown.tab="closeSelect"
                    @input="debounceSearch"
                >
                <ul id="options_list" ref="optionsList"
                    class="w-full bg-white border border-gray-400 -mt-px z-99999 text-xs overflow-y-auto max-h-64 absolute"
                    :class="{ 'compact-options' : compact }"
                    style="left:-1px; box-sizing: content-box;"
                    :style="position === 'bottom' ? `top:38px;` : `bottom:38px;`"
                    v-if="selectOpen">
                    <li v-for="(option, index) in filteredOptions"
                        @click="selectOption(option, index)"
                        :key="index"
                        class="px-3 py-2 text-gray-700 hover:bg-gray-300 cursor-pointer"
                        :class="{ 'selected-option': selected.label === option.label }">
                        {{ option.label ? option.label : option }}
                    </li>
                    <li class="px-3 py-2 text-gray-700 cursor-default" v-if="filteredOptions.length === 0">No options available</li>
                </ul>
            </div>
            <span class="text-xs text-red-700 absolute">{{ errors[0] }}</span>
            <input type="hidden" v-model="model">
        </ValidationProvider>
    </div>
</template>

<script>
    import ClickOutside from 'vue-click-outside';
    import axios from "axios";
    import _ from 'lodash'
    const VueScrollTo = require('vue-scrollto');

    export default {
        name: "InputSelect",
        props: {
            value: [String, Number, Object],
            label: { type: String, default: '' },
            initialValue: { default: '' },
            switchValue: { default: '' },
            name: { type: String, default: '' },
            placeholder: { type: String, default: '' },
            readonly: { type: Boolean, default: false },
            rules: { type: String, default: '' },
            required: { type: Boolean, default: false },
            tooltip: { type: String, default: '' },
            options: { default: () => [] },
            return: { type: String, default: 'value' },
            disabled: { type: Boolean, default: false },
            endpoint: { type: String, default: null },
            endpointLimit: { type: Number, default: 50 },
            endpointValueField: { type: String, default: 'id' },
            endpointLabelField: { type: String, default: 'name' },
            watchInitial: { type: Boolean, default: false },
            watchSwitch: { type: Boolean, default: false },
            emitData: { type: String, default: null },
            compact: { type: Boolean, default: false },
            type: { type: String, default: null },
            position: {
                type: String,
                default: 'bottom'
            },
        },
        directives: {
            ClickOutside
        },
        data: function () {
            return {
                model: null,
                search: '',
                selectOpen: false,
                userSelected: false,
                selected: {
                    index: null,
                    label: '',
                    value: ''
                },
                highlightedIndex: -1,
                startingIndex: null,
                loading: false,
                endpointOptions: [],
                fullEndpointOptions: []
            }
        },
        mounted() {
            this.mounted();
        },
        destroyed() {
            this.selected = {
                index: null,
                label: '',
                value: ''
            }
        },
        methods: {
            mounted (){
                let id;

                if (this.endpoint){
                    this.getEndpointData();
                }

                if (this.initialValue){
                    if (typeof this.initialValue === "object"){
                        id = this.initialValue.value;
                    } else if (typeof this.initialValue === "string" || typeof this.initialValue === "number"){
                        id = this.initialValue;
                    } else {
                        id = null;
                    }

                    if (id){
                        this.options.forEach((option, index) => {
                            if (option.value === id || option === id){
                                this.selected.index = index;

                                if (option.label){
                                    this.selected.label = option.label;
                                    this.selected.value = option.value;
                                } else {
                                    this.selected.label = option;
                                    this.selected.value = option;
                                }
                            }
                        });

                        // Used for Endpoint Initial Value Placement
                        if (this.endpoint && this.initialValue){
                            this.endpointOptions.forEach((option, index) => {
                                if (option.value === id || option === id){
                                    this.selected.index = index;

                                    if (option.label){
                                        this.selected.label = option.label;
                                        this.selected.value = option.value;
                                    } else {
                                        this.selected.label = option;
                                        this.selected.value = option;
                                    }
                                }
                            });

                            if (!this.selected.label || !this.selected.value){
                                this.selected.label = this.initialValue.label;
                                this.selected.value = this.initialValue.value;

                                this.endpointOptions.push(this.selected);
                            }
                        }

                        this.emitOption();
                    }
                }else if(typeof this.value !== 'undefined'){
                    const id = this.value
                    this.options.forEach((option, index) => {
                        if (option.value === id || option === id){
                            this.selected.index = index;

                            if (option.label){
                                this.selected.label = option.label;
                                this.selected.value = option.value;
                            } else {
                                this.selected.label = option;
                                this.selected.value = option;
                            }
                        }
                    });
                }
            },
            openSelect (){
                if (!this.disabled){
                    this.selectOpen = true;
                    this.$nextTick(() => this.$refs.searchInput.focus());
                }
            },
            closeSelect (){
                this.selectOpen = false;
                this.search = '';

                if (this.selected.label === ''){
                    this.highlightedIndex = -1;
                }
            },
            clearSelect (){

                this.selectOpen = false;
                this.search = '';
                this.selected.label = '';
                this.selected.value = '';
                this.highlightedIndex = -1;
                this.model= null;

                this.emitOption();
            },
            selectOption (option, index){
                let changed = false;

                if (this.selected){
                    if (this.selected.value !== option.value){
                        changed = true;
                    }
                }

                this.selected.index = index;

                if (option.label){
                    this.selected.label = option.label;
                    this.selected.value = option.value;
                } else {
                    this.selected.label = option;
                    this.selected.value = option;
                }

                this.selectOpen = false;
                this.search = '';
                this.highlightedIndex = index;

                if (changed) {
                    this.emitChangedOption();
                }

                this.emitOption();
            },
            navigateOptions (direction){
                if (!this.disabled) {
                    this.selectOpen = true;
                    const listItems = document.querySelectorAll("#options_list li");

                    if (listItems.length > 0) {

                        if (this.highlightedIndex > -1) {
                            listItems[this.highlightedIndex].classList.add("selected-option");
                        }

                        if (direction === 'down') {
                            if (this.highlightedIndex > -1) {
                                listItems[this.highlightedIndex].classList.remove("selected-option");
                            }

                            this.highlightedIndex = this.highlightedIndex < listItems.length - 1 ? ++this.highlightedIndex : listItems.length - 1;

                            if (this.highlightedIndex > -1) {
                                listItems[this.highlightedIndex].classList.add("selected-option");
                            }
                        } else if (direction === 'up') {
                            listItems[this.highlightedIndex].classList.remove("selected-option");

                            this.highlightedIndex = this.highlightedIndex > 0 ? --this.highlightedIndex : 0;
                            listItems[this.highlightedIndex].classList.add("selected-option");
                        }

                        this.$nextTick(() => {
                            const selectedOption = this.$refs.optionsList.querySelector('.selected-option');

                            if (selectedOption) {
                                VueScrollTo.scrollTo(selectedOption, 0, {
                                    container: '#options_list',
                                    offset: direction === 'up' ? -120 : -56,
                                    force: false
                                });
                            }
                        });
                    }
                }
            },
            selectOptionByKey (e){
                e.preventDefault();
                e.stopPropagation();

                this.selected.index = this.highlightedIndex;

                if (this.options[this.highlightedIndex].label){
                    this.selected.label = this.options[this.highlightedIndex].label;
                    this.selected.value = this.options[this.highlightedIndex].value;
                } else {
                    this.selected.label = this.options[this.highlightedIndex];
                    this.selected.value = this.options[this.highlightedIndex];
                }

                this.selectOpen = false;
                this.search = '';

                this.emitOption();
            },
            closeSelectByKey (e){
                this.highlightedIndex = this.startingIndex;

                this.selectOpen = false;
                this.search = '';
            },
            emitOption (){

                if (this.return === 'value'){
                    this.model = this.selected.value;
                } else if (this.return === 'object') {
                    this.model = this.fullEndpointOptions.find(o => o.id == this.selected.value);
                } else if (this.return === 'label') {
                    this.model = this.selected.label;
                } else {
                    this.model = {
                        label: this.selected.label,
                        value: this.selected.value
                    }
                }

                this.$emit('input', this.model);

                if (this.emitData){
                    let data = this.$store.getters[this.emitData].filter(option => {
                        return option.id === this.selected.value;
                    })[0];

                    this.$emit('data', data);
                }
            },
            emitChangedOption (){
                if (this.return === 'value'){
                    this.model = this.selected.value;
                } else if (this.return === 'object') {
                    this.model = this.fullEndpointOptions.find(o => o.id == this.selected.value);
                } else {
                    this.model = {
                        label: this.selected.label,
                        value: this.selected.value
                    }
                }

                this.$emit('update', this.model);
            },
            getEndpointData (){
                this.loading = true;

                axios.get(process.env.VUE_APP_API_URL + '/'+this.endpoint, {
                    params: {
                        filters: {
                            type: this.type,
                            search: {value: this.search }
                            },
                        limit: this.endpointLimit
                    }
                }).then(response => {
                    this.loading = false;

                    this.endpointOptions = response.data.map((option) => {
                        return { value: option[this.endpointValueField], label: option[this.endpointLabelField] }
                    });

                    this.fullEndpointOptions = response.data;
                });
            },
            destroyElement(){
                this.$destroy();
            },
            debounceSearch: _.debounce(function() {
                if (this.endpoint) {
                    this.getEndpointData();
                }
            }, 1000)
        },
        watch: {
            value: {
                handler(value){
                    if(typeof value !== 'undefined' && value !== this.selected.value){
                        const id = value
                        this.options.forEach((option, index) => {
                            if (option.value === id || option === id){
                                this.selectOption (option, index)
                            }
                        });
                    }
                },
                immediate: true
            },
            endpoint: function(newVal){
                this.clearSelect();
                this.getEndpointData();
            },
            selectOpen: function(newVal){
                if (newVal === true){
                    this.startingIndex = this.highlightedIndex;

                    this.$nextTick(() => {
                        const selectedOption = this.$refs.optionsList.querySelector('.selected-option');

                        if (selectedOption) {
                            VueScrollTo.scrollTo(selectedOption, 0, {
                                container: '#options_list',
                                offset: -56,
                                force: false
                            });
                        }
                    });
                }
            },
            options: function(newVal){
                this.mounted();
            },
            switchValue: function(newVal){
                if (this.watchSwitch && this.switchValue) {
                    let id;

                    if (typeof newVal === "object") {
                        id = newVal.value;
                    } else if (typeof newVal === "string" || typeof newVal === "number") {
                        id = newVal;
                    } else {
                        id = null;
                    }

                    if (id) {
                        this.options.forEach((option, index) => {
                            if (option.value === id || option === id) {
                                this.selected.index = index;

                                if (option.label) {
                                    this.selected.label = option.label;
                                    this.selected.value = option.value;
                                } else {
                                    this.selected.label = option;
                                    this.selected.value = option;
                                }
                            }
                        });

                        // Used for Endpoint Initial Value Placement
                        if (this.endpoint && newVal) {
                            this.endpointOptions.forEach((option, index) => {
                                if (option.value === id || option === id) {
                                    this.selected.index = index;

                                    if (option.label) {
                                        this.selected.label = option.label;
                                        this.selected.value = option.value;
                                    } else {
                                        this.selected.label = option;
                                        this.selected.value = option;
                                    }
                                }
                            });

                            if (!this.selected.label || !this.selected.value) {
                                this.selected.label = newVal.label;
                                this.selected.value = newVal.value;

                                this.endpointOptions.push(this.selected);
                            }
                        }

                        this.emitOption();
                    }
                }
            }
        },
        computed: {
            filteredOptions () {
                if (this.endpoint){
                    return this.endpointOptions;
                } else {
                    if (this.search !== ''){
                        return this.options.filter((item) => {
                            return item.label.toLowerCase().includes(this.search.toLowerCase());
                        });
                    } else {
                        return this.options;
                    }
                }
            }
        }
    }
</script>

<style scoped>
    .selected-option {
        background: #efefef;
    }
</style>
